import { api } from '../../lib/api.ts';

export type GsrStatus = 'DRAFT' | 'PENDING_SIGNATURE' | 'APPROVED' | 'RFQ_GENERATED';
export type GsrType = 'GOODS' | 'SERVICE';
export type PrStatus = 'DRAFT' | 'PENDING_SIGNATURE' | 'APPROVED';

export interface GsrItem {
  id: string;
  particulars: string;
  qtyRequested: number;
  unit: string;
  requiredDate: string | null;
  remarks: string | null;
  sortOrder: number;
}

export interface GsrPr {
  id: string;
  status: PrStatus;
  signDocId: string | null;
}

export interface GsrRequest {
  id: string;
  award?: { sentToFinanceAt: string | null; paid: boolean } | null;
}

export interface UserOption {
  id: string;
  name: string;
  nameDv: string | null;
  section: string | null;
}

export interface Gsr {
  id: string;
  gsrNumber: string;
  title: string | null;
  type: GsrType;
  status: GsrStatus;
  section: string | null;
  createdAt: string;
  signDocId: string | null;
  purchaseRequest: GsrPr | null;
  request: GsrRequest | null;
  items?: GsrItem[];
}

export interface GsrItemInput {
  particulars: string;
  qtyRequested: number;
  unit: string;
  requiredDate?: string;
  remarks?: string;
}

export interface GsrCreateInput {
  gsrNumber: string;
  title?: string;
  type: GsrType;
  requestedById?: string;
  approvedById: string;
  items: GsrItemInput[];
}

export const gsrApi = {
  list: () =>
    api.get<{ gsrs: Gsr[] }>('/gsr').then((r) => r.data.gsrs),
  get: (id: string) =>
    api.get<{ gsr: Gsr }>(`/gsr/${id}`).then((r) => r.data.gsr),
  // Active org accounts for the pickers. Pass a role to restrict (e.g. the PR
  // stockkeeper dropdown → only STOCKKEEPER accounts).
  userOptions: (role?: string) =>
    api.get<{ users: UserOption[] }>('/users/options', { params: role ? { role } : undefined }).then((r) => r.data.users),
  create: (input: GsrCreateInput) =>
    api.post<{ gsr: Gsr }>('/gsr', input).then((r) => r.data.gsr),
  sendForSigning: (id: string) =>
    api.post<{ editorPath: string }>(`/gsr/${id}/send-for-signing`).then((r) => r.data),
  withdrawSigning: (id: string) =>
    api.post<{ gsr: Gsr }>(`/gsr/${id}/withdraw-signing`).then((r) => r.data.gsr),
  // Stockkeeper generates the PR: a manual PR number, the in-charge stockkeeper,
  // and current stock for every item.
  createPurchaseRequest: (id: string, input: { prNumber: string; stockkeeperById: string; stock: Array<{ gsrItemId: string; qtyInStock: number }> }) =>
    api.post<{ purchaseRequest: { id: string } }>(`/gsr/${id}/purchase-request`, input).then((r) => r.data.purchaseRequest),
  generateRfq: (id: string) =>
    api.post<{ request: { id: string } }>(`/gsr/${id}/generate-rfq`).then((r) => r.data.request),
};
